import { useState } from "react";
import { Card } from "primereact/card";
import { InputSwitch } from "primereact/inputswitch";
import { Skeleton } from "primereact/skeleton";
import { TabPanel, TabView } from "primereact/tabview";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import AspenGuardianScans from "@/components/aspen/AspenGuardianScans";
import { useAspenFeatures } from "@/hooks/useAspenFeatures";

export default function AspenGuardian() {
  useBreadcrumb({
    label: "Aspen Guardian",
    route: AdminAppRoutes.routes.aspen.routes.guardian.route,
    display: "none",
    resetTrail: true,
  });

  const { features, isLoading, viewOnly, enableAspenGlobally, toggleFeature } = useAspenFeatures();
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  if (isLoading) return <Skeleton width="100%" height="20rem" />;

  return (
    <>
      <h1 className="mb-4">Aspen Guardian</h1>

      <TabView activeIndex={activeTabIndex} onTabChange={e => setActiveTabIndex(e.index)}>
        {features.guardian && (
          <TabPanel header="Overview">
            <AspenGuardianScans showInstructions={features.recordInstructionFiles} />
          </TabPanel>
        )}

        <TabPanel header="Guardian Settings">
          <Card pt={{ content: { className: "p-0" } }} className="mb-4">
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-surface-200">
              <strong>Aspen Guardian</strong>
              <InputSwitch
                checked={features.guardian}
                onChange={e => void toggleFeature("guardian", e.value)}
                disabled={viewOnly || !enableAspenGlobally}
              />
            </div>
            <p>
              Aspen Guardian can automatically update your AI instruction files based on
              vulnerability scan results, helping AI assistants learn from security issues to avoid
              them in future code generation.
            </p>
            <a
              href="https://github.com/securityJourney/aspen-connector"
              className={`block mt-4 ${themeClasses.textPrimaryDarker}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Connect your GitHub workflows with Aspen Guardian
            </a>
          </Card>

          <Card pt={{ content: { className: "p-0" } }} className="mb-4">
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div>
                  <strong>Record Instruction Files</strong>
                  <p className="body-sm text-secondary">
                    Enable reviewing updates to instructions after Aspen Guardian has processed them
                  </p>
                </div>
                <InputSwitch
                  checked={features.recordInstructionFiles}
                  onChange={e => void toggleFeature("recordInstructionFiles", e.value)}
                  disabled={viewOnly || !enableAspenGlobally}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <strong>Record CWEs with Aspen Adapt</strong>
                  <p className="body-sm text-secondary">
                    Forward CWE findings to Aspen Adapt to assign relevant content to your
                    developers
                  </p>
                </div>
                <InputSwitch
                  checked={features.forwardCwes}
                  onChange={e => void toggleFeature("forwardCwes", e.value)}
                  disabled={viewOnly || !enableAspenGlobally}
                />
              </div>
            </div>
          </Card>
        </TabPanel>
      </TabView>
    </>
  );
}
